import React, {useCallback, useEffect, useRef, useState} from 'react';
import {
    Autocomplete,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    FormControl,
    FormControlLabel,
    FormGroup,
    FormLabel,
    InputLabel,
    ListItemText,
    MenuItem,
    OutlinedInput,
    Select,
    Stack,
    TextField,
    Typography
} from '@mui/material';
import {useReportStore} from '../../../../../useReportStore';
import {apiSearchUnifiedTags} from '../../../../../../../utils/api/tags';
import {CLASSIFICATION_LEVELS} from '../../../../../../../components/prosemirror-editor/helpers/classificationLevels';

/** Document format (Sub Type) — both stores. Mirrors backend DocumentType enum. */
const SUB_TYPES = ['PDF', 'AUDIO', 'VIDEO', 'DOCX', 'IMAGE', 'HTML', 'OTHER'];

/** Report product types (report arm only) — maps to productTypes[] → report_segments.product_type. */
const PRODUCT_TYPES = [
    {value: 'pitch', label: 'Pitch'},
    {value: 'analytic', label: 'Analytic Product'},
    {value: 'tactical_sitrep', label: 'Tactical SITREP'},
    {value: 'strategic_sitrep', label: 'Strategic SITREP'},
    {value: 'iir', label: 'IIR'},
];

const SUB_TYPE_LABELS = {
    PDF: 'PDF',
    AUDIO: 'Audio',
    VIDEO: 'Video',
    DOCX: 'DOCX',
    IMAGE: 'Image',
    HTML: 'HTML',
    OTHER: 'Other',
};

const normalizeTag = (t) => {
    if (!t) return '';
    if (typeof t === 'string') return t;
    if (typeof t === 'object') return t.name || t.label || (t.id ? String(t.id) : '');
    return String(t);
};

const scopeIncludesReports = (scope) => scope === 'all' || scope === 'reports';

const labelForProductType = (value) =>
    PRODUCT_TYPES.find((c) => c.value === value)?.label || value;

/**
 * Collapsed view showing active filters summary
 */
const FiltersSummary = ({filters, onClear}) => {
    const parts = [];

    if (filters.tags?.length) {
        const shown = filters.tags.slice(0, 4).join(', ');
        const more = filters.tags.length > 4 ? ` +${filters.tags.length - 4}` : '';
        parts.push(`Tags: ${shown}${more}`);
    }
    if (filters.docSource) parts.push(`Source: ${filters.docSource}`);
    if (filters.productTypes?.length) {
        parts.push(`Product Type: ${filters.productTypes.map(labelForProductType).join(', ')}`);
    }
    if (filters.subTypes?.length) {
        parts.push(`Sub Type: ${filters.subTypes.map((t) => SUB_TYPE_LABELS[t] || t).join(', ')}`);
    }
    if (filters.classificationLevels?.length) {
        parts.push(`Classification: ${filters.classificationLevels.join(', ')}`);
    }

    if (parts.length === 0) return null;

    return (
        <Typography variant="body2" color="text.secondary">
            Applied filters: {parts.join(' · ')}
            <Typography
                component="span"
                variant="text"
                onClick={onClear}
                ml={1}
                sx={{cursor: 'pointer', textDecoration: 'underline', '&:hover': {color: 'primary.main'}}}
            >
                Clear
            </Typography>
        </Typography>
    );
};

/**
 * Tag autocomplete with async search
 */
const TagsFilter = ({value, onChange}) => {
    const [options, setOptions] = useState([]);
    const [inputValue, setInputValue] = useState('');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        let active = true;

        const fetchTags = async () => {
            setLoading(true);
            try {
                // Unified autocomplete: deduped names across both tag catalogs
                // (Corpus/Products `tags` + My Data/Highlights `data_item_tag`).
                const data = await apiSearchUnifiedTags(inputValue);
                if (active) {
                    setOptions(Array.isArray(data) ? data.map(normalizeTag).filter(Boolean) : []);
                }
            } catch {
                if (active) setOptions([]);
            } finally {
                if (active) setLoading(false);
            }
        };

        const timer = setTimeout(fetchTags, inputValue ? 300 : 0);
        return () => {
            active = false;
            clearTimeout(timer);
        };
    }, [inputValue]);

    return (
        <Autocomplete
            multiple
            options={options}
            value={value}
            onChange={(_, newValue) => onChange(newValue)}
            inputValue={inputValue}
            onInputChange={(_, newInput) => setInputValue(newInput)}
            filterSelectedOptions
            loading={loading}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label="Tags"
                    placeholder="Search or add tags"
                    size="small"
                    slotProps={{
                        input: {
                            ...params.InputProps,
                            endAdornment: (
                                <>
                                    {loading && <CircularProgress color="inherit" size={16}/>}
                                    {params.InputProps.endAdornment}
                                </>
                            ),
                        },
                    }}
                    fullWidth
                />
            )}
        />
    );
};

/**
 * Generic multi-select dropdown for orthogonal filter dimensions.
 */
const MultiSelectFilter = ({label, labelId, value, options, onChange, getOptionLabel}) => {
    const resolveLabel = getOptionLabel || ((opt) => (typeof opt === 'string' ? opt : opt.label));
    const resolveValue = (opt) => (typeof opt === 'string' ? opt : opt.value);

    return (
        <FormControl size="small" fullWidth>
            <InputLabel id={labelId}>{label}</InputLabel>
            <Select
                labelId={labelId}
                multiple
                value={value}
                onChange={(e) => onChange(
                    typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value,
                )}
                input={<OutlinedInput label={label}/>}
                renderValue={(selected) => (
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                        {selected.map((v) => {
                            const opt = options.find((o) => resolveValue(o) === v);
                            return <Chip key={v} size="small" label={opt ? resolveLabel(opt) : v}/>;
                        })}
                    </Stack>
                )}
            >
                {options.map((opt) => {
                    const optValue = resolveValue(opt);
                    return (
                        <MenuItem key={optValue} value={optValue}>
                            <Checkbox checked={value.includes(optValue)}/>
                            <ListItemText primary={resolveLabel(opt)}/>
                        </MenuItem>
                    );
                })}
            </Select>
        </FormControl>
    );
};

/**
 * Classification level checkboxes
 */
const ClassificationFilter = ({value, onChange}) => (
    <FormControl component="fieldset" size="small">
        <FormLabel component="legend" sx={{mb: 1, fontSize: '0.875rem', fontWeight: 600}}>
            Classification Level
        </FormLabel>
        <FormGroup row>
            {CLASSIFICATION_LEVELS.map((level) => (
                <FormControlLabel
                    key={level.value}
                    control={
                        <Checkbox
                            checked={value.includes(level.value)}
                            onChange={(e) => {
                                if (e.target.checked) {
                                    onChange([...value, level.value]);
                                } else {
                                    onChange(value.filter(v => v !== level.value));
                                }
                            }}
                        />
                    }
                    label={level.value}
                />
            ))}
        </FormGroup>
    </FormControl>
);

/**
 * SearchFilters component - manages filter state and syncs to store with debounce.
 *
 * `sourceScope` (All / Corpus / My Data / Reports) controls which orthogonal filter
 * dimensions are shown: Product Type appears for All/Reports. Sub Type, tags,
 * classification level, and domain are always available.
 */
const SearchFilters = ({sourceScope = 'all'}) => {
    const showFilters = useReportStore(s => s.showFilters);

    // Store values - use individual selectors to avoid object recreation
    const storeTagsRaw = useReportStore(s => s.tags);
    const storeTags = storeTagsRaw || [];
    const storeDocSource = useReportStore(s => s.docSource) || '';
    const storeSubTypesRaw = useReportStore(s => s.subTypes);
    const storeSubTypes = storeSubTypesRaw || [];
    const storeProductTypesRaw = useReportStore(s => s.productTypes);
    const storeProductTypes = storeProductTypesRaw || [];
    const storeClassificationLevelsRaw = useReportStore(s => s.classificationLevels);
    const storeClassificationLevels = storeClassificationLevelsRaw || [];

    // Store setters
    const setTags = useReportStore(s => s.setTags);
    const setDocSource = useReportStore(s => s.setDocSource);
    const setSubTypes = useReportStore(s => s.setSubTypes);
    const setProductTypes = useReportStore(s => s.setProductTypes);
    const setClassificationLevels = useReportStore(s => s.setClassificationLevels);
    const clearFiltersStore = useReportStore(s => s.clearFilters);

    const showProductType = scopeIncludesReports(sourceScope);

    // Local state for immediate UI feedback
    const [localFilters, setLocalFilters] = useState(() => ({
        tags: storeTags.map(normalizeTag).filter(Boolean),
        docSource: storeDocSource,
        subTypes: storeSubTypes,
        productTypes: storeProductTypes,
        classificationLevels: storeClassificationLevels,
    }));

    // Track if we're syncing from store to prevent loops
    const isSyncingFromStoreRef = useRef(false);

    // Sync from store when it changes externally
    useEffect(() => {
        const tags = storeTagsRaw || [];
        const classificationLevels = storeClassificationLevelsRaw || [];

        isSyncingFromStoreRef.current = true;
        setLocalFilters({
            tags: tags.map(normalizeTag).filter(Boolean),
            docSource: storeDocSource,
            subTypes: storeSubTypesRaw || [],
            productTypes: storeProductTypesRaw || [],
            classificationLevels: classificationLevels,
        });
        // Reset flag after state update
        requestAnimationFrame(() => {
            isSyncingFromStoreRef.current = false;
        });
    }, [
        storeTagsRaw,
        storeDocSource,
        storeSubTypesRaw,
        storeProductTypesRaw,
        storeClassificationLevelsRaw,
    ]);

    // Debounced sync to store (skip if syncing from store)
    useEffect(() => {
        if (isSyncingFromStoreRef.current) return undefined;

        const timer = setTimeout(() => {
            setTags(localFilters.tags);
            setDocSource(localFilters.docSource);
            setSubTypes(localFilters.subTypes);
            setProductTypes(localFilters.productTypes);
            setClassificationLevels(localFilters.classificationLevels);
        }, 250);

        return () => clearTimeout(timer);
    }, [
        localFilters,
        setTags,
        setDocSource,
        setSubTypes,
        setProductTypes,
        setClassificationLevels,
    ]);

    const updateFilter = useCallback((key, value) => {
        setLocalFilters(prev => ({...prev, [key]: value}));
    }, []);

    const handleClear = useCallback(() => {
        setLocalFilters({
            tags: [],
            docSource: '',
            subTypes: [],
            productTypes: [],
            classificationLevels: [],
        });
        clearFiltersStore();
    }, [clearFiltersStore]);

    // Collapsed view
    if (!showFilters) {
        return <FiltersSummary filters={localFilters} onClear={handleClear}/>;
    }

    return (
        <Stack sx={{border: '1px solid #e0e0e0', borderRadius: 1, p: 2}} spacing={2}>
            <TagsFilter
                value={localFilters.tags}
                onChange={(v) => updateFilter('tags', v)}
            />

            {showProductType && (
                <MultiSelectFilter
                    label="Product Type"
                    labelId="product-type-label"
                    value={localFilters.productTypes}
                    options={PRODUCT_TYPES}
                    onChange={(v) => updateFilter('productTypes', v)}
                />
            )}

            <MultiSelectFilter
                label="Sub Type"
                labelId="sub-type-label"
                value={localFilters.subTypes}
                options={SUB_TYPES}
                getOptionLabel={(opt) => SUB_TYPE_LABELS[opt] || opt}
                onChange={(v) => updateFilter('subTypes', v)}
            />

            <TextField
                label="Source (domain)"
                placeholder="e.g. en.wikipedia.org"
                value={localFilters.docSource}
                onChange={(e) => updateFilter('docSource', e.target.value)}
                size="small"
                fullWidth
            />

            <ClassificationFilter
                value={localFilters.classificationLevels}
                onChange={(v) => updateFilter('classificationLevels', v)}
            />

            <Stack direction="row" spacing={1} justifyContent="flex-start">
                <Button variant="text" onClick={handleClear}>Clear Filters</Button>
            </Stack>
        </Stack>
    );
};

export default SearchFilters;
